import React, { useEffect, useRef, useState } from "react";
import "@mui/icons-material";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import SearchText from "./SearchText";
import ChatInput from "./ChatInput";
import {
  AutoAwesomeOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { Typography } from "@mui/material";

const Chat = ({
  selectedApplicationName,
  setActiveView,
  togglePopup,
  conversations,
  processedMessages,
  selectedApplicationId,
  handleSendReply,
  senderEmail,
  error,
  loadingRewrite,
}) => {
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false); // New state for accordion
  const searchBoxRef = useRef(null);
  const messagesEndRef = useRef(null);

  const filteredMessages = processedMessages?.filter((message) => {
    if (!searchQuery.trim()) return true;
    return message.body.toLowerCase().includes(searchQuery.toLowerCase());
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target)
      ) {
        setShowSearchOverlay(false);
      }
    }

    if (showSearchOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchOverlay]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [filteredMessages]);

  const handleDeleteRecent = (index) => {
    const updated = [...recentSearches];
    updated.splice(index, 1);
    setRecentSearches(updated);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery && !recentSearches.includes(trimmedQuery)) {
      setRecentSearches((prev) => [trimmedQuery, ...prev.slice(0, 4)]);
    }
    setShowSearchOverlay(false);
  };

  const toggleSummary = () => {
    setIsSummaryExpanded(!isSummaryExpanded); // Toggle the accordion
  };
  console.log(filteredMessages);
  return (
    <>
      {/* Header */}
      <div className="bg-black text-white flex items-center justify-between px-4 py-3">
        {showSearchOverlay && (
          <SearchText
            handleDeleteRecent={handleDeleteRecent}
            handleSearchSubmit={handleSearchSubmit}
            searchBoxRef={searchBoxRef}
            recentSearches={recentSearches}
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
          />
        )}

        <div
          className="flex items-center space-x-3 cursor-pointer"
          onClick={() => setActiveView("details")}
        >
          <img
            src={"/img/images.png"}
            alt="Logo"
            className="w-10 h-10 rounded-full bg-white p-1"
          />
          <h1 className="text-lg font-semibold">{selectedApplicationName}</h1>
        </div>
        <div className="flex items-center space-x-3">
          <SearchIcon
            className="text-white cursor-pointer"
            onClick={() => setShowSearchOverlay(true)}
          />
          <CloseIcon
            className="text-white cursor-pointer"
            onClick={togglePopup}
          />
        </div>
      </div>

      {loadingRewrite && (
        <div className="text-center py-2 text-sm text-gray-500">
          Rewriting message...
        </div>
      )}

      {error && <Typography color="error">{error}</Typography>}
      {/* Summary - Accordion style */}
      {conversations?.history?.summary && (
        <div className="bg-orange-200 py-2 px-4 text-sm opacity-70 w-full">
          <div
            className="font-bold mb-1 cursor-pointer flex items-center justify-between"
            onClick={toggleSummary} // Toggle accordion on click
          >
            <span className="text-base">
              <AutoAwesomeOutlined
                className="text-orange-700"
                fontSize="small"
              />
              AI Summary
            </span>
            {isSummaryExpanded ? (
              <KeyboardArrowUp className="ml-2" fontSize="small" />
            ) : (
              <KeyboardArrowDown className="ml-2" fontSize="small" />
            )}
          </div>
          <div
            className={`text-sm ${isSummaryExpanded ? "" : "line-clamp-2"}`}
            style={{ whiteSpace: isSummaryExpanded ? "normal" : "nowrap" }}
          >
            {conversations.history.summary}
          </div>
          <div className="text-xs mt-1 cursor-pointer" onClick={toggleSummary}>
            {isSummaryExpanded ? "Show Less" : "Show More"}
          </div>
        </div>
      )}

      {/* Chat body */}
      <div
        className="flex-1 overflow-y-auto px-4 py-2 space-y-6"
        style={{
          height: "100%",
          overflowY: "auto",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none",
          color: "#ffffff",
        }}
      >
        {filteredMessages &&
          filteredMessages.map((message, index) => {
            const prevMessage = filteredMessages[index - 1];
            const showDate =
              !prevMessage ||
              prevMessage.formattedDate !== message.formattedDate;

            return (
              <div key={index}>
                {showDate && (
                  <div className="text-center my-4">
                    <span className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow">
                      {message.formattedDate}
                    </span>
                  </div>
                )}
                <div
                  className={`flex ${
                    message.isSender ? "justify-end" : "justify-start"
                  }`}
                >
                  {!message.isSender ? (
                    <div className="flex items-start space-x-2 max-w-[75%]">
                      <div
                        className="flex-shrink-0 bg-[#b63b57] text-white w-8 h-8 min-w-[2rem] min-h-[2rem] flex items-center justify-center rounded-full text-sm font-medium cursor-pointer"
                        title={message.name || "Unknown User"}
                      >
                        {message.name ? message.name.charAt(0) : "B"}
                      </div>
                      <div className="bg-white rounded-xl px-4 py-3 shadow text-sm break-words">
                        {message.name && (
                          <p className="text-[#b63b57] font-medium">
                            {message.name}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          From: {message.sender_email}
                        </p>
                        <p className="text-black mt-2">{message.body}</p>
                        {message.attachments?.map((file, i) => {
                          const isObject =
                            typeof file === "object" && file !== null;
                          const url = isObject
                            ? file.previewUrl || file.url
                            : file;
                          const name = isObject ? file.name : "Download file";
                          const type = isObject ? file.type : "";

                          const isImage =
                            type.startsWith("image/") ||
                            url.startsWith("blob:");
                          const isPDF =
                            type === "application/pdf" || url.endsWith(".pdf");

                          return (
                            <div key={i} className="mt-2">
                              {isImage ? (
                                <img
                                  src={url}
                                  alt={name}
                                  className="max-w-[200px] rounded-lg"
                                />
                              ) : isPDF ? (
                                <iframe
                                  src={url}
                                  title={name}
                                  className="w-full max-w-[300px] h-[400px] rounded-lg border"
                                />
                              ) : (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline text-sm"
                                >
                                  📎 {name}
                                </a>
                              )}
                            </div>
                          );
                        })}

                        <p className="text-xs text-right text-gray-500 mt-1">
                          {message.formattedTime}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-end space-y-2 max-w-[75%]">
                      <div className="bg-orange-200 text-black text-sm p-3 px-4 rounded-xl max-w-xs break-words">
                        <p className="text-xs text-gray-500 mb-1">
                          From: {message.sender_email}
                        </p>
                        {message.body}
                        {message.attachments?.map((file, i) => {
                          const isObject =
                            typeof file === "object" && file !== null;
                          const url = isObject
                            ? file.previewUrl || file.url
                            : file;
                          const name = isObject ? file.name : "Download file";
                          const type = isObject ? file.type : "";

                          const isImage =
                            type.startsWith("image/") ||
                            url.startsWith("blob:");
                          const isPDF =
                            type === "application/pdf" || url.endsWith(".pdf");

                          return (
                            <div key={i} className="mt-2">
                              {isImage ? (
                                <img
                                  src={url}
                                  alt={name}
                                  className="max-w-[200px] rounded-lg"
                                />
                              ) : isPDF ? (
                                <iframe
                                  src={url}
                                  title={name}
                                  className="w-full max-w-[300px] h-[400px] rounded-lg border"
                                />
                              ) : (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline text-sm"
                                >
                                  📎 {name}
                                </a>
                              )}
                            </div>
                          );
                        })}

                        <p className="text-right text-xs mt-1">
                          {message.formattedTime}
                          <span className="ml-1">
                            <DoneAllRoundedIcon fontSize="10px" />
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <ChatInput
        conversations={conversations}
        selectedApplicationId={selectedApplicationId}
        handleSendReply={handleSendReply}
        senderEmail={senderEmail}
      />
    </>
  );
};

export default Chat;
